import * as React from 'react';
import { ChevronsUpDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ComboboxList, type ComboboxOption } from './combobox-list';
import {
  multiHandlers,
  renderMultiTrigger,
  resolveSelection,
  singleHandlers,
  type ResolvedSelection,
  type SelectHandlers,
} from './combobox-multi';

export type { ComboboxOption } from './combobox-list';

const SEARCHABLE_THRESHOLD = 5;

interface SharedProps {
  options: readonly ComboboxOption[];
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyMessage?: string | undefined;
  loadingMessage?: string | undefined;
  searchable?: boolean | undefined;
  searchValue?: string | undefined;
  onSearchChange?: ((q: string) => void) | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  clearable?: boolean | undefined;
  className?: string | undefined;
  id?: string | undefined;
  'aria-invalid'?: boolean | 'true' | 'false' | undefined;
  'aria-describedby'?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
}

export interface SingleComboboxProps extends SharedProps {
  multi?: false | undefined;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  // Opt-in: offer an "Add '<typed>'" action for a value not in `options`.
  onCreate?: ((label: string) => void) | undefined;
}

export interface MultiComboboxProps extends SharedProps {
  multi: true;
  value: readonly string[];
  onChange: (next: string[]) => void;
}

export type ComboboxProps = SingleComboboxProps | MultiComboboxProps;

interface SingleTriggerArgs {
  open: boolean;
  label: string;
  hasValue: boolean;
  invalid: boolean;
  disabled: boolean | undefined;
  id: string | undefined;
  describedBy: string | undefined;
  ariaLabel: string | undefined;
  ariaLabelledBy: string | undefined;
  className: string | undefined;
}

function renderSingleTrigger({
  open,
  label,
  hasValue,
  invalid,
  disabled,
  id,
  describedBy,
  ariaLabel,
  ariaLabelledBy,
  className,
}: Readonly<SingleTriggerArgs>): React.JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      id={id}
      disabled={disabled}
      className={cn(
        'h-10 w-full justify-between px-3 font-normal',
        !hasValue && 'text-muted-foreground',
        invalid && 'border-destructive ring-2 ring-destructive/40 ring-offset-2',
        className,
      )}
    >
      {/* min-w-0 lets the flex child shrink so `truncate` engages; without it a
          long selected label overflows the fixed-width trigger and collides
          with adjacent fields. */}
      <span className="min-w-0 truncate">{label}</span>
      <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
    </Button>
  );
}

interface ShellArgs {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.JSX.Element;
  list: React.JSX.Element;
  title: string;
}

function PopoverShell({ open, onOpenChange, trigger, list }: Readonly<ShellArgs>) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width) p-0"
      >
        {list}
      </PopoverContent>
    </Popover>
  );
}

function SheetShell({ open, onOpenChange, trigger, list, title }: Readonly<ShellArgs>) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent side="bottom" className="flex h-[80svh] flex-col gap-0 p-0">
        <SheetHeader className="border-b">
          <SheetTitle className="text-base">{title}</SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1">{list}</div>
      </SheetContent>
    </Sheet>
  );
}

function isInvalid(value: ComboboxProps['aria-invalid']): boolean {
  return value === true || value === 'true';
}

interface UseComboboxState {
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  isAsync: boolean;
  showSearch: boolean;
  selection: ResolvedSelection;
  handlers: SelectHandlers;
}

function useComboboxState(props: Readonly<ComboboxProps>): UseComboboxState {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const isAsync = props.onSearchChange !== undefined;
  // A creatable picker always shows its input — the "Add" row only renders
  // for a typed query, so hiding the input below the threshold makes the
  // create action unreachable on a short list.
  const creatable = props.multi !== true && props.onCreate !== undefined;
  const showSearch =
    props.searchable ?? (isAsync || creatable || props.options.length > SEARCHABLE_THRESHOLD);
  const selection = resolveSelection(props.value, props.options);
  // Live ref so multi-pick handlers see post-clear state even when RHF skips a re-render.
  const setRef = React.useRef(selection.set);
  setRef.current = selection.set;
  const close = () => {
    setOpen(false);
    if (props.onSearchChange !== undefined) props.onSearchChange('');
  };
  const handlers =
    props.multi === true
      ? multiHandlers(props.onChange, props.onSearchChange, () => setRef.current, close)
      : singleHandlers(props.onChange, close);
  return { open, setOpen, isMobile, isAsync, showSearch, selection, handlers };
}

function buildTrigger(
  props: Readonly<ComboboxProps>,
  state: Readonly<UseComboboxState>,
  placeholder: string,
): React.JSX.Element {
  const invalid = isInvalid(props['aria-invalid']);
  if (props.multi === true) {
    return renderMultiTrigger({
      open: state.open,
      selected: state.selection.list,
      placeholder,
      invalid,
      disabled: props.disabled,
      id: props.id,
      describedBy: props['aria-describedby'],
      className: props.className,
      onRemove: state.handlers.onRemove,
    });
  }
  const single = state.selection.list[0];
  return renderSingleTrigger({
    open: state.open,
    label: single?.label ?? placeholder,
    hasValue: state.selection.set.size > 0,
    invalid,
    disabled: props.disabled,
    id: props.id,
    describedBy: props['aria-describedby'],
    ariaLabel: props['aria-label'],
    ariaLabelledBy: props['aria-labelledby'],
    className: props.className,
  });
}

export function Combobox(props: Readonly<ComboboxProps>) {
  const state = useComboboxState(props);
  const placeholder = props.placeholder ?? 'Select…';
  const trigger = buildTrigger(props, state, placeholder);
  // Creatable is single-select only, async or not; closing after create mirrors onPick.
  let onCreate: ((label: string) => void) | undefined;
  if (props.multi !== true && props.onCreate !== undefined) {
    const create = props.onCreate;
    onCreate = (label: string) => {
      create(label);
      state.setOpen(false);
    };
  }
  const list = (
    <ComboboxList
      selected={state.selection.set}
      options={props.options}
      showSearch={state.showSearch}
      isAsync={state.isAsync}
      multi={props.multi === true}
      clearable={props.clearable ?? true}
      searchPlaceholder={props.searchPlaceholder ?? 'Search…'}
      searchValue={props.searchValue}
      onSearchChange={props.onSearchChange}
      emptyMessage={props.emptyMessage ?? 'No results.'}
      loading={props.loading}
      loadingMessage={props.loadingMessage ?? 'Loading…'}
      onSelect={state.handlers.onPick}
      onClear={state.handlers.onClear}
      onCreate={onCreate}
    />
  );
  const shell = {
    open: state.open,
    onOpenChange: state.setOpen,
    trigger,
    list,
    title: placeholder,
  };
  return state.isMobile ? <SheetShell {...shell} /> : <PopoverShell {...shell} />;
}
